/* Ares Acres — DJI Doctor intent resolver
 * ---------------------------------------------------------------------------
 * Adds the component / failure-mode layer the diagnostic engine lacks.
 *
 * The engine's intake() scores SYMPTOMS only. A query like "propeller broken"
 * matches no symptom phrase, scores below the confidence floor on keywords
 * alone, and therefore falls through to systems[0] — which is why the customer
 * was shown "Interpreting as: a Propulsion problem" and a generic symptom menu.
 * Propulsion is the parent system; it must not stand in for a recognised
 * component and condition.
 *
 * This module runs BEFORE the engine's symptom classifier and resolves:
 *     Aircraft -> System -> Component -> Failure mode -> Position -> Context
 * It never invents a part, material number, test or outcome. It only maps free
 * text onto a closed vocabulary; everything downstream stays deterministic and
 * the validated engine rules are untouched.
 *
 * Precedence ladder (spec §DOCTOR-INTENT-UNDERSTANDING-B):
 *   1 material_number  2 error_code  3 failure_phrase  4 component+condition
 *   5 component+behaviour  6 component  7 symptom  8 system  9 unknown
 *
 * Global: window.AresIntent
 */
(function (root) {
  'use strict';

  /* ----------------------------------------------------------- normalisation */

  // Material numbers and error codes must survive normalisation intact, so we
  // normalise case/whitespace/unicode but keep '.', '-' and digits.
  function normalize(s) {
    return String(s == null ? '' : s)
      .normalize ? String(s == null ? '' : s).normalize('NFKD') : String(s == null ? '' : s);
  }

  function clean(s) {
    var t = normalize(s)
      .replace(/[‘’‛]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/[‐-―]/g, '-')
      .toLowerCase()
      .replace(/[^a-z0-9.\-'/ ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return t;
  }

  function tokens(s) {
    return clean(s).split(' ').filter(Boolean);
  }

  /* ------------------------------------------------------------- vocabularies */

  // DJI material number, e.g. YC.JG.ZS003588 / BC.AG.SS001140 / YC.JG.QX004794
  var MATERIAL_RE = /\b[A-Z]{2}\.[A-Z]{2}\.[A-Z]{0,3}\d{3,}(?:\.\d{2})?\b/i;
  // App-style error codes, e.g. "Motor 3 ESC error" carries a position, while
  // codes proper look like 0x1A2B, E-1234, ESC 0308, "error code 30245".
  var ERROR_CODE_RE = /\b(?:0x[0-9a-f]{3,}|e-?\d{3,}|(?:error\s*(?:code)?\s*)(\d{3,}))\b/i;

  // component id -> { label, system, syn: [...] }
  // Synonyms include the misspellings operators actually type.
  var COMPONENTS = {
    propeller: {
      label: 'Propeller', system: 'propulsion',
      syn: ['propeller', 'propellers', 'propellor', 'propellors', 'propellar', 'propeler',
            'prop', 'props', 'blade', 'blades', 'rotor blade', 'rotor blades', 'airscrew']
    },
    motor: {
      label: 'Motor', system: 'propulsion',
      syn: ['motor', 'motors', 'moter', 'propulsion motor', 'brushless motor']
    },
    esc: {
      label: 'ESC', system: 'propulsion',
      syn: ['esc', 'escs', 'electronic speed controller', 'speed controller']
    },
    arm: {
      label: 'Arm', system: 'airframe',
      syn: ['arm', 'arms', 'aircraft arm', 'frame arm', 'boom']
    },
    landing_gear: {
      label: 'Landing gear', system: 'airframe',
      syn: ['landing gear', 'leg', 'legs', 'landing leg', 'skid', 'skids']
    },
    pump: {
      label: 'Pump', system: 'spraying',
      syn: ['pump', 'pumps', 'water pump', 'spray pump', 'diaphragm pump']
    },
    nozzle: {
      label: 'Nozzle', system: 'spraying',
      syn: ['nozzle', 'nozzles', 'sprinkler', 'sprinklers', 'spray head', 'atomizer', 'atomiser']
    },
    flow_meter: {
      label: 'Flow meter', system: 'spraying',
      syn: ['flow meter', 'flowmeter', 'flow sensor']
    },
    tank: {
      label: 'Tank', system: 'spraying',
      syn: ['tank', 'tanks', 'spray tank', 'water tank']
    },
    battery: {
      label: 'Battery', system: 'power',
      syn: ['battery', 'batteries', 'batery', 'flight battery', 'intelligent battery', 'cell', 'cells']
    },
    charger: {
      label: 'Charger', system: 'power',
      syn: ['charger', 'chargers', 'charging hub', 'charge hub']
    },
    generator: {
      label: 'Generator', system: 'generator',
      syn: ['generator', 'genset', 'gen set', 'd12000ie', 'd14000ie', '12000ie', '14000ie']
    },
    rtk: {
      label: 'RTK module', system: 'navigation',
      syn: ['rtk', 'd-rtk', 'drtk', 'rtk module', 'gnss', 'gps antenna']
    },
    radar: {
      label: 'Radar', system: 'navigation',
      syn: ['radar', 'radars', 'obstacle radar', 'terrain radar']
    },
    remote_controller: {
      label: 'Remote controller', system: 'remote',
      syn: ['remote', 'remote controller', 'controller', 'rc', 'rc plus', 'transmitter']
    },
    spreader: {
      label: 'Spreader', system: 'spreading',
      syn: ['spreader', 'spreading system', 'spinner', 'auger']
    }
  };

  // failure mode id -> { label, syn, safety }
  var FAILURE_MODES = {
    physical_damage: {
      label: 'Physical damage', safety: 'ground',
      syn: ['broken', 'break', 'broke', 'cracked', 'crack', 'cracks', 'chipped', 'chip',
            'split', 'splits', 'bent', 'bend', 'snapped', 'snap', 'damaged', 'damage',
            'shattered', 'shatter', 'fractured', 'torn', 'tear', 'deformed', 'warped']
    },
    vibration: {
      label: 'Vibration', safety: 'ground',
      syn: ['vibrates', 'vibrate', 'vibrating', 'vibration', 'vibrations', 'wobble',
            'wobbles', 'wobbling', 'shaking', 'shakes', 'shake', 'unbalanced',
            'imbalance', 'out of balance', 'oscillation']
    },
    stuck_stiff: {
      label: 'Stuck or obstructed', safety: null,
      syn: ['stuck', 'stiff', 'seized', 'seizing', 'binding', 'binds', 'jammed', 'jam',
            'hard to turn', 'will not spin', 'wont spin', 'does not spin', 'rough',
            'notchy', 'gritty', 'grinding']
    },
    no_power: {
      label: 'No power', safety: null,
      syn: ['no power', 'will not turn on', 'wont turn on', 'does not turn on', 'dead',
            'not powering', 'no lights', 'will not boot', 'wont boot']
    },
    error_reported: {
      label: 'Error reported', safety: null,
      syn: ['error', 'errors', 'fault', 'alarm', 'warning', 'alert', 'throttle loss',
            'code', 'error code', 'abnormal']
    },
    leak: {
      label: 'Leak', safety: null,
      syn: ['leak', 'leaks', 'leaking', 'dripping', 'drips', 'weeping', 'seeping']
    },
    no_output: {
      label: 'No output', safety: null,
      syn: ['not spraying', 'no spray', 'stopped spraying', 'not spreading', 'no flow',
            'no output', 'nothing comes out', 'not dispensing', 'blocked', 'clogged']
    },
    noise: {
      label: 'Abnormal noise', safety: null,
      syn: ['noise', 'noisy', 'buzzing', 'whining', 'whine', 'clicking', 'rattle',
            'rattling', 'squeal', 'squealing', 'abnormal sound', 'strange sound']
    },
    overheat: {
      label: 'Overheating', safety: null,
      syn: ['overheat', 'overheats', 'overheating', 'too hot', 'burning smell', 'hot to touch']
    },
    not_detected: {
      label: 'Not detected', safety: null,
      syn: ['not detected', 'not recognised', 'not recognized', 'no signal', 'disconnected',
            'not connecting', 'will not pair', 'wont pair', 'not found']
    }
  };

  // Context markers that change which questions matter.
  var CONTEXTS = {
    collision: ['collision', 'collided', 'crash', 'crashed', 'impact', 'hit a', 'hit the',
                'struck', 'clipped', 'went down', 'flipped'],
    in_flight: ['in flight', 'while flying', 'during flight', 'mid flight', 'midflight',
                'in the air', 'while spraying'],
    on_ground: ['on the ground', 'at power-on', 'at power on', 'before takeoff', 'preflight',
                'pre-flight', 'when starting'],
    after_maintenance: ['after replacing', 'after i replaced', 'after service', 'after maintenance',
                        'after installing', 'newly installed', 'just replaced', 'after repair']
  };

  // Negation cues. Scoped to the clause they appear in.
  var NEG_CUES = ['not', "isn't", 'isnt', "aren't", 'arent', "doesn't", 'doesnt', 'no',
                  'never', 'without', 'nothing', "wasn't", 'wasnt', 'neither'];

  var MODEL_RE = /\b(t100|t70p|t70|t55|t50|t40|t30|t25|t20p|t20)\b/i;

  /* ---------------------------------------------------------------- matching */

  // Longest-phrase-first index so "rotor blade" beats "blade", and
  // "flow meter" is never split into unrelated tokens.
  function buildIndex(dict) {
    var out = [];
    Object.keys(dict).forEach(function (id) {
      (dict[id].syn || []).forEach(function (phrase) {
        out.push({ id: id, phrase: ' ' + clean(phrase) + ' ', words: clean(phrase).split(' ').length });
      });
    });
    return out.sort(function (a, b) { return b.words - a.words || b.phrase.length - a.phrase.length; });
  }

  var COMP_INDEX = buildIndex(COMPONENTS);
  var FAIL_INDEX = buildIndex(FAILURE_MODES);

  function findAll(index, padded) {
    var hits = [];
    index.forEach(function (e) {
      var at = padded.indexOf(e.phrase);
      if (at >= 0 && !hits.some(function (h) { return h.id === e.id; })) {
        hits.push({ id: e.id, at: at, words: e.words, phrase: e.phrase.trim() });
      }
    });
    return hits.sort(function (a, b) { return a.at - b.at; });
  }

  // Controlled fuzzy matching: only for known technical terms of 6+ characters,
  // edit distance 1, and only when nothing matched exactly. Never applied to
  // material numbers or short tokens, where a single character is meaningful.
  function editDistance1(a, b) {
    if (Math.abs(a.length - b.length) > 1) return false;
    var i = 0, j = 0, diff = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) { i++; j++; continue; }
      if (++diff > 1) return false;
      if (a.length > b.length) i++;
      else if (a.length < b.length) j++;
      else { i++; j++; }
    }
    return diff + (a.length - i) + (b.length - j) <= 1;
  }

  function fuzzyComponent(toks) {
    var best = null;
    toks.forEach(function (w) {
      if (w.length < 6) return;
      COMP_INDEX.forEach(function (e) {
        if (best || e.words !== 1) return;
        var p = e.phrase.trim();
        if (p.length >= 6 && editDistance1(w, p)) best = { id: e.id, phrase: w, fuzzy: true };
      });
    });
    return best;
  }

  /* ---------------------------------------------------------------- position */

  function findPosition(text) {
    var t = clean(text);
    var m;
    // "motor 3", "m3", "arm 2", "no. 4"
    m = /\b(?:motor|arm|m|no\.?|number)\s*([1-8])\b/.exec(t);
    if (m) return { kind: 'numbered', value: m[1], label: 'position ' + m[1] };
    // "rear left", "front-right"
    m = /\b(front|rear|back)[ -]?(left|right)\b/.exec(t);
    if (m) return { kind: 'quadrant', value: m[1] + '-' + m[2], label: (m[1] === 'back' ? 'rear' : m[1]) + ' ' + m[2] };
    m = /\b(left|right)[ -]?(front|rear|back)\b/.exec(t);
    if (m) return { kind: 'quadrant', value: m[2] + '-' + m[1], label: (m[2] === 'back' ? 'rear' : m[2]) + ' ' + m[1] };
    m = /\b(upper|lower|top|bottom)\b/.exec(t);
    if (m) return { kind: 'level', value: m[1], label: m[1] };
    m = /\b(left|right)\b/.exec(t);
    if (m) return { kind: 'side', value: m[1], label: m[1] + ' side' };
    return null;
  }

  function findQuantity(text) {
    var m = /\b(one|two|three|four|1|2|3|4|5|6|7|8)\s+(?:props?|propellers?|motors?|blades?|arms?)\b/.exec(clean(text));
    if (!m) return null;
    var map = { one: 1, two: 2, three: 3, four: 4 };
    return map[m[1]] || parseInt(m[1], 10) || null;
  }

  /* ------------------------------------------------------------------ clauses */

  // Split on punctuation and contrastive conjunctions so negation stays local:
  // "propeller is not broken, motor is stiff" must not suppress "stiff".
  //
  // clean() deliberately drops ',' and ';' (they are noise for phrase matching),
  // so clause boundaries must be found BEFORE cleaning or they disappear and
  // negation leaks across the whole sentence. A '.' only ends a clause when
  // followed by whitespace or end-of-string, otherwise it would split a
  // material number such as YC.JG.ZS003588.
  function clauses(text) {
    return String(text == null ? '' : text)
      .split(/\s*(?:[;,]|\.(?=\s|$)|\bbut\b|\bhowever\b|\bthough\b|\band\b)\s*/i)
      .map(function (s) { return clean(s); })
      .filter(Boolean);
  }

  function isNegated(clause) {
    var w = clause.split(' ');
    return w.some(function (x) { return NEG_CUES.indexOf(x) >= 0; });
  }

  /* -------------------------------------------------------------------- parse */

  function parse(text, opts) {
    opts = opts || {};
    var raw = String(text == null ? '' : text);
    var padded = ' ' + clean(raw) + ' ';
    var toks = tokens(raw);

    var res = {
      raw: raw,
      level: 9,
      route: 'unknown',
      aircraft: opts.model || null,
      aircraftFromText: null,
      system: null,
      systemLabel: null,
      component: null,
      componentLabel: null,
      failureMode: null,
      failureLabel: null,
      position: null,
      quantity: null,
      contexts: [],
      materialNumber: null,
      errorCode: null,
      negated: [],
      fuzzy: false,
      ambiguous: false,
      safety: null,
      breadcrumb: null,
      summary: null
    };

    /* aircraft from text overrides nothing; UI state wins unless text is explicit */
    var mm = MODEL_RE.exec(raw);
    if (mm) {
      res.aircraftFromText = mm[1].toUpperCase();
      if (!opts.model) res.aircraft = res.aircraftFromText;
    }

    /* 1 — exact DJI material number. Route straight to part lookup; do NOT run
       symptom classification, per spec test case 6. */
    var matMatch = MATERIAL_RE.exec(raw);
    if (matMatch) {
      res.materialNumber = matMatch[0].toUpperCase();
      res.level = 1;
      res.route = 'material_number';
      res.summary = 'Material number ' + res.materialNumber;
      return finish(res);
    }

    /* 2 — explicit error code */
    var ecMatch = ERROR_CODE_RE.exec(raw);
    if (ecMatch) {
      res.errorCode = (ecMatch[1] || ecMatch[0]).toUpperCase();
      res.level = 2;
      res.route = 'error_code';
    }

    /* clause-scoped component + failure extraction with negation */
    var cls = clauses(raw);
    var positive = [];
    cls.forEach(function (c) {
      var p = ' ' + c + ' ';
      var comps = findAll(COMP_INDEX, p);
      var fails = findAll(FAIL_INDEX, p);
      var neg = isNegated(c);
      if (!comps.length && !fails.length) return;
      if (neg) {
        comps.forEach(function (cm) {
          fails.forEach(function (f) { res.negated.push({ component: cm.id, failureMode: f.id }); });
          if (!fails.length) res.negated.push({ component: cm.id, failureMode: null });
        });
        if (!comps.length) fails.forEach(function (f) { res.negated.push({ component: null, failureMode: f.id }); });
        return;
      }
      positive.push({ comps: comps, fails: fails, clause: c });
    });

    /* pick the strongest positive clause: one that carries both a component and
       a failure mode beats a bare component, which beats a bare failure mode. */
    var pair = null, compOnly = null, failOnly = null;
    positive.forEach(function (p) {
      if (p.comps.length && p.fails.length && !pair) pair = p;
      else if (p.comps.length && !compOnly) compOnly = p;
      else if (p.fails.length && !failOnly) failOnly = p;
    });

    var chosenComp = null, chosenFail = null;
    if (pair) { chosenComp = pair.comps[0]; chosenFail = pair.fails[0]; res.level = Math.min(res.level, 4); res.route = 'component_condition'; }
    else if (compOnly) { chosenComp = compOnly.comps[0]; res.level = Math.min(res.level, 6); res.route = 'component'; }
    else if (failOnly) { chosenFail = failOnly.fails[0]; res.level = Math.min(res.level, 7); res.route = 'symptom'; }

    /* controlled fuzzy fallback for a mistyped technical term */
    if (!chosenComp && !res.errorCode) {
      var fz = fuzzyComponent(toks);
      if (fz) {
        chosenComp = fz; res.fuzzy = true;
        var anyFail = findAll(FAIL_INDEX, padded);
        var stillNegated = res.negated.some(function (n) { return n.component === fz.id; });
        if (anyFail.length && !stillNegated) { chosenFail = anyFail[0]; res.level = Math.min(res.level, 4); res.route = 'component_condition'; }
        else { res.level = Math.min(res.level, 6); res.route = 'component'; }
      }
    }

    if (chosenComp) {
      res.component = chosenComp.id;
      res.componentLabel = COMPONENTS[chosenComp.id].label;
      res.system = COMPONENTS[chosenComp.id].system;
    }
    if (chosenFail) {
      res.failureMode = chosenFail.id;
      res.failureLabel = FAILURE_MODES[chosenFail.id].label;
      res.safety = FAILURE_MODES[chosenFail.id].safety;
    }

    /* 8 — bare system term with nothing else recognised */
    if (!res.component && !res.failureMode && !res.errorCode) {
      var sysHit = null;
      [['propulsion', ['propulsion']], ['spraying', ['spraying', 'spray system']],
       ['power', ['power', 'battery system']], ['spreading', ['spreading']],
       ['navigation', ['navigation', 'nav']], ['remote', ['remote', 'firmware']],
       ['airframe', ['airframe', 'frame']], ['generator', ['generator']]].forEach(function (s) {
        if (sysHit) return;
        s[1].forEach(function (p) { if (padded.indexOf(' ' + p + ' ') >= 0) sysHit = s[0]; });
      });
      if (sysHit) { res.system = sysHit; res.level = 8; res.route = 'system'; }
    }

    res.position = findPosition(raw);
    res.quantity = findQuantity(raw);
    Object.keys(CONTEXTS).forEach(function (k) {
      CONTEXTS[k].forEach(function (p) {
        if (padded.indexOf(' ' + clean(p) + ' ') >= 0 && res.contexts.indexOf(k) < 0) res.contexts.push(k);
      });
    });

    /* A failure mode with no component is ambiguous by definition — it needs one
       discriminating question, not a wall of symptom buttons. */
    if (res.failureMode && !res.component) res.ambiguous = true;

    return finish(res);
  }

  var SYSTEM_LABELS = {
    propulsion: 'Propulsion', spraying: 'Spraying system', power: 'Power & battery',
    spreading: 'Spreading system', navigation: 'Navigation', remote: 'Remote controller',
    airframe: 'Airframe', generator: 'Generator'
  };

  function finish(res) {
    res.systemLabel = res.system ? (SYSTEM_LABELS[res.system] || res.system) : null;

    var crumb = [];
    if (res.aircraft) crumb.push(res.aircraft);
    if (res.systemLabel) crumb.push(res.systemLabel);
    if (res.componentLabel) crumb.push(res.componentLabel);
    if (res.failureLabel) crumb.push(res.failureLabel);
    res.breadcrumb = crumb.length ? crumb.join(' → ') : null;

    // The customer-facing line skips the parent system: the spec requires
    // "We understood: T100 → Propeller → Physical damage", with Propulsion
    // available only as breadcrumb context.
    var sum = [];
    if (res.aircraft) sum.push(res.aircraft);
    if (res.componentLabel) sum.push(res.componentLabel);
    if (res.failureLabel) sum.push(res.failureLabel);
    if (!res.summary) res.summary = sum.length > 1 ? sum.join(' → ') : null;

    res.resolved = !!(res.component || res.materialNumber || res.errorCode);
    return res;
  }

  /* ------------------------------------------------------------ model gating */

  // Diagnosis is only offered for models with validated rules. Everything else
  // routes to parts lookup or a technician — never a fabricated diagnosis.
  function coverage(model, validatedModels) {
    var m = String(model || '').toUpperCase();
    var list = (validatedModels || []).map(function (x) { return String(x).toUpperCase(); });
    return list.indexOf(m) >= 0 ? 'diagnostic' : 'lookup_only';
  }

  root.AresIntent = {
    parse: parse,
    coverage: coverage,
    normalize: clean,
    tokens: tokens,
    COMPONENTS: COMPONENTS,
    FAILURE_MODES: FAILURE_MODES,
    SYSTEM_LABELS: SYSTEM_LABELS,
    MATERIAL_RE: MATERIAL_RE
  };
})(typeof window !== 'undefined' ? window : this);
